import { useState } from 'react';
import SEOHead from '../../components/base/SEOHead';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    vehicle: '',
    service: '',
    insurance: false,
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone || !formData.email || !formData.message) {
      alert('Please fill in all required fields');
      return;
    }

    if (formData.message.length > 500) {
      alert('Message must be 500 characters or less');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('phone', formData.phone);
      formBody.append('email', formData.email);
      formBody.append('vehicle', formData.vehicle);
      formBody.append('service', formData.service);
      formBody.append('insurance', formData.insurance ? 'Yes' : 'No');
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d4nggjd0qurnbdguf3g0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          vehicle: '',
          service: '',
          insurance: false,
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  const schema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "mainEntity": {
      "@type": "AutoRepair",
      "name": "Brooklyn Auto Body Shop",
      "telephone": "+16319337887",
      "email": "info@collisionexpertsnyc.com",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "4001 2nd Avenue",
        "addressLocality": "Brooklyn",
        "addressRegion": "NY",
        "postalCode": "11232",
        "addressCountry": "US"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 40.6782,
        "longitude": -73.9442
      },
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          "opens": "08:00",
          "closes": "18:00"
        },
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": "Saturday",
          "opens": "09:00",
          "closes": "15:00"
        }
      ]
    }
  };

  return (
    <>
      <SEOHead
        title="Contact Brooklyn Auto Body Shop - Free Estimates & 24/7 Service"
        description="Contact Brooklyn Auto Body Shop for free estimates. Call (631) 933-7887 for 24/7 emergency service. Located at 4001 2nd Avenue, Brooklyn, NY 11232. Work with all insurance companies."
        keywords="contact auto body shop Brooklyn, free car repair estimate, 24/7 auto service NY, Brooklyn mechanic contact, emergency auto repair"
        canonical={`${siteUrl}/contact`}
        ogTitle="Contact Brooklyn Auto Body Shop - Free Estimates & 24/7 Service"
        ogDescription="Contact Brooklyn Auto Body Shop for free estimates. Call (631) 933-7887 for 24/7 emergency service. Located at 4001 2nd Avenue, Brooklyn, NY 11232."
        geoPosition="40.6782;-73.9442"
        geoPlacename="Brooklyn, NY"
        geoRegion="US-NY"
        lastModified={new Date().toISOString()}
        schema={schema}
      />
      <div className="min-h-screen bg-white font-['Inter']">
        <Navbar />
        
        <main className="pt-24">
          <section className="relative py-20 bg-gradient-to-br from-orange-600 to-orange-700">
            <div className="max-w-7xl mx-auto px-6 text-center">
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-4 font-['Playfair_Display']">
                Contact Us
              </h1>
              <p className="text-xl text-white/95 max-w-2xl mx-auto">
                Get your free estimate today. Our experienced mechanics are ready to help with all your auto repair needs.
              </p>
            </div>
          </section>

          <section className="py-20 bg-gray-50">
            <div className="max-w-7xl mx-auto px-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-6 font-['Playfair_Display']">
                    Get Your Free Estimate
                  </h2>
                  <p className="text-gray-600 mb-8">
                    Fill out the form and we'll get back to you within 24 hours. For immediate assistance, call us at (631) 933-7887.
                  </p>

                  <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-600 focus:border-transparent text-sm"
                          placeholder="John Smith"
                        />
                      </div>

                      <div>
                        <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                          Phone Number *
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-600 focus:border-transparent text-sm"
                          placeholder="(631) 933-7887"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-600 focus:border-transparent text-sm"
                        placeholder="john@example.com"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="vehicle" className="block text-sm font-semibold text-gray-700 mb-2">
                          Vehicle Info
                        </label>
                        <input
                          type="text"
                          id="vehicle"
                          name="vehicle"
                          value={formData.vehicle}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-600 focus:border-transparent text-sm"
                          placeholder="2020 Honda Accord"
                        />
                      </div>

                      <div>
                        <label htmlFor="service" className="block text-sm font-semibold text-gray-700 mb-2">
                          Service Needed
                        </label>
                        <select
                          id="service"
                          name="service"
                          value={formData.service}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-600 focus:border-transparent text-sm"
                        >
                          <option value="">Select a service</option>
                          <option value="Collision Repair">Collision Repair</option>
                          <option value="Paint & Body Work">Paint & Body Work</option>
                          <option value="Dent Removal">Dent Removal</option>
                          <option value="Mechanical Service">Mechanical Service</option>
                          <option value="Maintenance">Maintenance</option>
                          <option value="Insurance Claim">Insurance Claim</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                        Describe Your Issue * (Max 500 characters)
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        maxLength={500}
                        rows={5}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-600 focus:border-transparent text-sm resize-none"
                        placeholder="Please describe the issue with your vehicle..."
                      ></textarea>
                      <p className="text-xs text-gray-500 mt-1">
                        {formData.message.length}/500 characters
                      </p>
                    </div>

                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="insurance"
                        name="insurance"
                        checked={formData.insurance}
                        onChange={handleChange}
                        className="mt-1 w-5 h-5 text-orange-600 border-gray-300 rounded focus:ring-orange-600 cursor-pointer"
                      />
                      <label htmlFor="insurance" className="text-sm text-gray-700 cursor-pointer">
                        This is an insurance claim
                      </label>
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-orange-600 hover:bg-orange-700 text-white px-8 py-4 rounded-lg text-lg font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit Request'}
                    </button>

                    {submitStatus === 'success' && (
                      <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                        <strong>Success!</strong> Your request has been submitted. We'll contact you within 24 hours.
                      </div>
                    )}

                    {submitStatus === 'error' && (
                      <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                        <strong>Error!</strong> Something went wrong. Please try again or call us directly.
                      </div>
                    )}
                  </form>
                </div>

                <div>
                  <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-6 font-['Playfair_Display']">
                      Contact Information
                    </h3>

                    <div className="space-y-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-lg flex-shrink-0">
                          <i className="ri-phone-fill text-2xl text-orange-600"></i>
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-1">Phone</h4>
                          <a href="tel:+16319337887" className="text-gray-600 hover:text-orange-600 transition-colors">
                            (631) 933-7887
                          </a>
                          <p className="text-sm text-gray-500 mt-1">24/7 Emergency Service</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-lg flex-shrink-0">
                          <i className="ri-mail-fill text-2xl text-orange-600"></i>
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-1">Email</h4>
                          <a href="mailto:info@collisionexpertsnyc.com" className="text-gray-600 hover:text-orange-600 transition-colors">
                            info@collisionexpertsnyc.com
                          </a>
                        </div>
                      </div>

                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-lg flex-shrink-0">
                          <i className="ri-map-pin-fill text-2xl text-orange-600"></i>
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-1">Address</h4>
                          <p className="text-gray-600">
                            4001 2nd Avenue<br />
                            Brooklyn, NY 11232
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-lg flex-shrink-0">
                          <i className="ri-time-fill text-2xl text-orange-600"></i>
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-1">Business Hours</h4>
                          <p className="text-gray-600 text-sm">
                            Monday - Friday: 8:00 AM - 6:00 PM<br />
                            Saturday: 9:00 AM - 3:00 PM<br />
                            Sunday: Closed
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-orange-600 to-orange-700 rounded-2xl p-8 text-white">
                    <h3 className="text-2xl font-bold mb-4 font-['Playfair_Display']">
                      Why Choose Us?
                    </h3>
                    <ul className="space-y-3">
                      <li className="flex items-start gap-3">
                        <i className="ri-check-line text-2xl flex-shrink-0"></i>
                        <span>30+ years of experience in Brooklyn</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-check-line text-2xl flex-shrink-0"></i>
                        <span>Work with all insurance companies</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-check-line text-2xl flex-shrink-0"></i>
                        <span>Free estimates and consultations</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-check-line text-2xl flex-shrink-0"></i>
                        <span>Same-day service available</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-check-line text-2xl flex-shrink-0"></i>
                        <span>Certified and experienced mechanics</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="py-16 bg-white">
            <div className="max-w-7xl mx-auto px-6">
              <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center font-['Playfair_Display']">
                Find Us in Brooklyn
              </h2>
              <div className="rounded-2xl overflow-hidden shadow-lg h-96">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d193595.15830869428!2d-74.119763973046!3d40.697663747874!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c24416947c2109%3A0x82765c7404007886!2sBrooklyn%2C%20NY!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Brooklyn Auto Body Shop Location"
                ></iframe>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default ContactPage;
